import type { VenueDetails } from "common/types";
import type { AccessibilityCategory } from "common/types";

type ComplexCategoriesData = {
  name: string;
  display_name?: string;
};

type AccessibilityFeature = { name: string };

function normaliseAccessibility(
  basic: AccessibilityCategory[] | undefined,
  enterprise: ComplexCategoriesData[] | undefined,
): AccessibilityFeature[] {
  if (basic?.length) {
    // The page hides general-user tags awaiting approval, so Google must not get them either.
    return basic.flatMap((cat) =>
      cat.tags.flatMap((group) =>
        group.qtags
          .filter(
            (t) =>
              t.user_roles?.isGeneralUser !== true ||
              t.approved_for_public === true,
          )
          .map((t) => ({ name: t.tagName })),
      ),
    );
  }
  if (enterprise?.length)
    return enterprise.map((c) => ({ name: c.display_name ?? c.name }));
  return [];
}

export function buildPlaceSchema({
  venue,
  basic,
  enterprise,
  origin,
  slug,
}: {
  venue: VenueDetails | undefined;
  basic: AccessibilityCategory[] | undefined;
  enterprise: ComplexCategoriesData[] | undefined;
  origin: string;
  slug: string;
}) {
  const features = normaliseAccessibility(basic, enterprise);
  const streetAddress = venue?.route;
  const addressLocality =
    venue?.locality ?? venue?.postalTown ?? venue?.city ?? venue?.cityLocation;

  return {
    "@context": "https://schema.org",
    "@type": "Place",
    name: venue?.name,
    description: venue?.description,
    url: `${origin}/venuedetails/${slug}`,
    address: streetAddress
      ? {
          "@type": "PostalAddress",
          streetAddress,
          addressLocality,
          addressCountry: "GB",
        }
      : undefined,
    geo:
      venue?.latitude && venue?.longitude
        ? {
            "@type": "GeoCoordinates",
            latitude: venue.latitude,
            longitude: venue.longitude,
          }
        : undefined,
    amenityFeature: features.map((f) => ({
      "@type": "LocationFeatureSpecification",
      name: f.name,
      value: true,
    })),
  };
}
